import { useState, useEffect, useCallback } from "react";
import { Badge, Popover, List, Avatar, Typography, Button, Spin, Empty, Tabs } from "antd";
import { BellOutlined, CheckCircleOutlined, ClockCircleOutlined, MessageOutlined } from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import 'dayjs/locale/ar';
import { useTranslation } from "react-i18next";
import { db } from "../../firebase";
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc, writeBatch } from "firebase/firestore";

dayjs.extend(relativeTime);

const { Text, Title } = Typography;

const cleanNotificationMessage = (msg) => {
    if (!msg) return "";
    let current = String(msg).trim();
    
    // De-serialize multiple times if it is double-encoded
    for (let i = 0; i < 5; i++) {
        if ((current.startsWith('{') && current.endsWith('}')) || (current.startsWith('"') && current.endsWith('"')) || (current.startsWith('[') && current.endsWith(']'))) {
            try {
                const parsed = JSON.parse(current);
                if (typeof parsed === 'string') {
                    current = parsed.trim();
                } else if (typeof parsed === 'object' && parsed !== null) {
                    current = parsed;
                    break;
                } else {
                    break;
                }
            } catch (e) {
                break;
            }
        } else {
            break;
        }
    }

    if (typeof current === 'object' && current !== null) {
        let textContent = "";
        const extractText = (node) => {
            if (node.text) textContent += node.text + " ";
            if (Array.isArray(node.children)) node.children.forEach(extractText);
        };
        if (current.root) {
            extractText(current.root);
            return textContent.trim();
        }
    }

    return String(current).replace(/<[^>]*>/g, '').trim();
};

const translateNotification = (item, t) => {
    const type = item.type;
    const arabicTitle = item.title || "";
    const arabicMessage = item.message || "";
    
    let displayTitle = arabicTitle;
    let displayMessage = arabicMessage;

    const cleanMsg = cleanNotificationMessage(arabicMessage);

    try {
        switch (type) {
            case 'lead_assigned': {
                displayTitle = t('notification.lead_assigned.title', { defaultValue: 'تم تعيينك كمسؤول عن Lead جديد' });
                const match = cleanMsg.match(/^(.+?) أضاف Lead: (.+?) وعيّنك كمسؤول عنه$/);
                if (match) {
                    displayMessage = t('notification.lead_assigned.message', {
                        creator: match[1],
                        leadName: match[2],
                        defaultValue: `${match[1]} أضاف Lead: ${match[2]} وعيّنك كمسؤول عنه`
                    });
                } else {
                    displayMessage = cleanMsg;
                }
                break;
            }
            case 'lead_owner_changed': {
                displayTitle = t('notification.lead_owner_changed.title', { defaultValue: 'تم تعيينك كمسؤول عن Lead' });
                const match = cleanMsg.match(/^(.+?) عيّنك كمسؤول عن Lead: (.+?)$/);
                if (match) {
                    displayMessage = t('notification.lead_owner_changed.message', {
                        creator: match[1],
                        leadName: match[2],
                        defaultValue: `${match[1]} عيّنك كمسؤول عن Lead: ${match[2]}`
                    });
                } else {
                    displayMessage = cleanMsg;
                }
                break;
            }
            case 'lead_converted': {
                displayTitle = t('notification.lead_converted.title', { defaultValue: 'تم تحويل Lead إلى Deal' });
                const match = cleanMsg.match(/^(.+?) قام بتحويل Lead: (.+?) وأنت المسؤول عن الـ Deal الجديد$/);
                if (match) {
                    displayMessage = t('notification.lead_converted.message', {
                        creator: match[1],
                        leadName: match[2],
                        defaultValue: `${match[1]} قام بتحويل Lead: ${match[2]} وأنت المسؤول عن الـ Deal الجديد`
                    });
                } else {
                    displayMessage = cleanMsg;
                }
                break;
            }
            case 'deal_assigned': {
                displayTitle = t('notification.deal_assigned.title', { defaultValue: 'تم تعيينك كمسؤول عن Deal جديد' });
                const match = cleanMsg.match(/^(.+?) أنشأ Deal: (.+?) وعيّنك كمسؤول عنه$/);
                if (match) {
                    displayMessage = t('notification.deal_assigned.message', {
                        creator: match[1],
                        dealName: match[2],
                        defaultValue: `${match[1]} أنشأ Deal: ${match[2]} وعيّنك كمسؤول عنه`
                    });
                } else {
                    displayMessage = cleanMsg;
                }
                break;
            }
            case 'deal_owner_changed': {
                displayTitle = t('notification.deal_owner_changed.title', { defaultValue: 'تم تعيينك كمسؤول عن Deal' });
                const match = cleanMsg.match(/^(.+?) عيّنك كمسؤول عن Deal: (.+?)$/);
                if (match) {
                    displayMessage = t('notification.deal_owner_changed.message', {
                        creator: match[1],
                        dealName: match[2],
                        defaultValue: `${match[1]} عيّنك كمسؤول عن Deal: ${match[2]}`
                    });
                } else {
                    displayMessage = cleanMsg;
                }
                break;
            }
            case 'deal_stage_changed': {
                displayTitle = t('notification.deal_stage_changed.title', { defaultValue: 'تغيير مرحلة الـ Deal' });
                const match = cleanMsg.match(/^(.+?) قام بتغيير مرحلة الـ Deal \((.+?)\) إلى (.+?)$/);
                if (match) {
                    displayMessage = t('notification.deal_stage_changed.message', {
                        creator: match[1],
                        dealName: match[2],
                        stageName: match[3],
                        defaultValue: `${match[1]} قام بتغيير مرحلة الـ Deal (${match[2]}) إلى ${match[3]}`
                    });
                } else {
                    displayMessage = cleanMsg;
                }
                break;
            }
            case 'note_assigned': {
                displayTitle = t('notification.note_assigned.title', { defaultValue: 'تم إسناد ملاحظة جديدة إليك' });
                const match1 = cleanMsg.match(/^(.+?) أضاف ملاحظة: (.+?)$/);
                const match2 = cleanMsg.match(/^(.+?) قام بتعديل وإسناد ملاحظة إليك: (.+?)$/);
                if (match1) {
                    displayMessage = t('notification.note_assigned.message_add', {
                        creator: match1[1],
                        titleStr: match1[2],
                        defaultValue: `${match1[1]} أضاف ملاحظة: ${match1[2]}`
                    });
                } else if (match2) {
                    displayMessage = t('notification.note_assigned.message_edit', {
                        creator: match2[1],
                        titleStr: match2[2],
                        defaultValue: `${match2[1]} قام بتعديل وإسناد ملاحظة إليك: ${match2[2]}`
                    });
                } else {
                    displayMessage = cleanMsg;
                }
                break;
            }
            case 'note_mention': {
                displayTitle = t('notification.note_mention.title', { defaultValue: 'تمت الإشارة إليك في ملاحظة' });
                const match = cleanMsg.match(/^(.+?) أشار إليك في الملاحظة: (.+?)$/);
                if (match) {
                    displayMessage = t('notification.note_mention.message', {
                        creator: match[1],
                        titleStr: match[2],
                        defaultValue: `${match[1]} أشار إليك في الملاحظة: ${match[2]}`
                    });
                } else {
                    displayMessage = cleanMsg;
                }
                break;
            }
            case 'task_assigned': {
                displayTitle = t('notification.task_assigned.title', { defaultValue: 'تم إسناد مهمة جديدة إليك' });
                const match1 = cleanMsg.match(/^(.+?) أسند إليك المهمة: (.+?)$/);
                const match2 = cleanMsg.match(/^تم إسناد المهمة إليك: (.+?)$/);
                if (match1) {
                    displayMessage = t('notification.task_assigned.message_by_user', {
                        creator: match1[1],
                        taskName: match1[2],
                        defaultValue: `${match1[1]} أسند إليك المهمة: ${match1[2]}`
                    });
                } else if (match2) {
                    displayMessage = t('notification.task_assigned.message_by_system', {
                        taskName: match2[1],
                        defaultValue: `تم إسناد المهمة إليك: ${match2[1]}`
                    });
                } else {
                    displayMessage = cleanMsg;
                }
                break;
            }
            case 'task_mention': {
                displayTitle = t('notification.task_mention.title', { defaultValue: 'تمت الإشارة إليك في مهمة' });
                const match = cleanMsg.match(/^(.+?) أشار إليك في المهمة: (.+?)$/);
                if (match) {
                    displayMessage = t('notification.task_mention.message', {
                        creator: match[1],
                        taskName: match[2],
                        defaultValue: `${match[1]} أشار إليك في المهمة: ${match[2]}`
                    });
                } else {
                    displayMessage = cleanMsg;
                }
                break;
            }
            case 'announcement': {
                const match = arabicTitle.match(/^إعلان جديد: (.+?)$/);
                if (match) {
                    displayTitle = t('notification.announcement.title', {
                        title: match[1],
                        defaultValue: `إعلان جديد: ${match[1]}`
                    });
                } else {
                    displayTitle = t('notification.announcement.default_title', { defaultValue: 'إعلان جديد' });
                }
                displayMessage = cleanMsg;
                break;
            }
            default: {
                displayTitle = arabicTitle;
                displayMessage = cleanMsg;
                break;
            }
        }
    } catch (e) {
        console.error("Translation processing error:", e);
    }

    return { title: displayTitle, message: displayMessage };
};

const NotificationCenter = () => {
    const { t, i18n } = useTranslation();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [now, setNow] = useState(dayjs()); // Used to trigger re-renders for relative time
    const navigate = useNavigate();

    const userId = localStorage.getItem("id");
    const roleId = parseInt(localStorage.getItem("roleId"));
    const recipientType = roleId === 9 ? 'agent' : 'user';
    const recipientKey = `${recipientType}_${userId}`;

    useEffect(() => {
        console.log("🔔 [NotificationCenter] Initializing with recipientKey:", recipientKey, "userId:", userId);
        if (!userId) {
            console.log("🔔 [NotificationCenter] userId is empty, skipping listener");
            return;
        }

        setLoading(true);
        const notifRef = collection(db, "notifications", recipientKey, "items");
        const q = query(notifRef, orderBy("createdAt", "desc"), limit(50));

        console.log("🔔 [NotificationCenter] Subscribing to Firestore path:", `notifications/${recipientKey}/items`);

        const unsubscribe = onSnapshot(q, (snapshot) => {
            console.log("🔔 [NotificationCenter] Received snapshot, size =", snapshot.size);
            const notifs = snapshot.docs.map(doc => {
                const data = doc.data();
                const date = data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt ? new Date(data.createdAt) : new Date());
                return {
                    id: doc.id,
                    ...data,
                    created_at: date,
                    is_read: data.isRead ? 1 : 0
                };
            });
            console.log("🔔 [NotificationCenter] Mapped notifications:", notifs);
            setNotifications(notifs);
            setUnreadCount(notifs.filter(n => !n.isRead).length);
            setLoading(false);
        }, (error) => {
            console.error("🔔 [NotificationCenter] Firestore onSnapshot error:", error);
            setLoading(false);
        });

        const interval = setInterval(() => {
            setNow(dayjs());
        }, 60000);

        return () => {
            console.log("🔔 [NotificationCenter] Unsubscribing");
            unsubscribe();
            clearInterval(interval);
        };
    }, [userId, recipientKey]);

    useEffect(() => {
        dayjs.locale(i18n.language);
    }, [i18n.language]);

    const handleMarkAsRead = async (id) => {
        try {
            const docRef = doc(db, "notifications", recipientKey, "items", String(id));
            await updateDoc(docRef, { isRead: true });
            await axios.put(`/notification/${id}/read`);
        } catch (error) {
            console.error("Failed to mark as read", error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            const batch = writeBatch(db);
            const unreadNotifs = notifications.filter(n => !n.isRead);
            for (const n of unreadNotifs) {
                const docRef = doc(db, "notifications", recipientKey, "items", String(n.id));
                batch.update(docRef, { isRead: true });
            }
            await batch.commit();
            await axios.put("/notification/read-all");
        } catch (error) {
            console.error("Failed to mark all as read", error);
        }
    };

    // Normalize legacy notification links to current frontend routes
    const normalizeNotificationLink = (notification) => {
        let link = notification.link;

        // If we have a link, fix legacy paths
        if (link) {
            // Fix old /leadDetails/:id → /admin/lead/:id
            link = link.replace(/^\/leadDetails\/(\d+)/, '/admin/lead/$1');
            // Fix old /dealDetails/:id → /admin/deal/:id
            link = link.replace(/^\/dealDetails\/(\d+)/, '/admin/deal/$1');
            // Fix old /contactDetails/:id → /admin/contact/:id
            link = link.replace(/^\/contactDetails\/(\d+)/, '/admin/contact/$1');
            // Fix paths missing /admin prefix
            if (link === '/task' || link === '/announcement') {
                link = `/admin${link}`;
            }
            // Ensure /admin prefix exists for all paths
            if (link.startsWith('/') && !link.startsWith('/admin/')) {
                link = `/admin${link}`;
            }
            return link;
        }

        // Fallback: construct link from notification type + metadata
        const meta = notification.metadata || {};
        switch (notification.type) {
            case 'lead_assigned':
            case 'lead_owner_changed':
                if (meta.leadId) return `/admin/lead/${meta.leadId}`;
                return '/admin/lead';
            case 'lead_converted':
            case 'deal_assigned':
            case 'deal_owner_changed':
            case 'deal_stage_changed':
                if (meta.dealId) return `/admin/deal/${meta.dealId}`;
                return '/admin/deal';
            case 'task_assigned':
            case 'task_mention':
                if (meta.dealId) return `/admin/deal/${meta.dealId}`;
                if (meta.leadId) return `/admin/lead/${meta.leadId}`;
                return '/admin/task';
            case 'note_assigned':
            case 'note_mention':
                if (meta.dealId) return `/admin/deal/${meta.dealId}`;
                if (meta.contactId) return `/admin/contact/${meta.contactId}`;
                return '/admin/task';
            case 'announcement':
                return '/admin/announcement';
            default:
                return null;
        }
    };

    const handleNotificationClick = (notification) => {
        if (!notification.is_read && !notification.isRead) {
            handleMarkAsRead(notification.id);
        }
        if (notification.type === 'chat') {
            // Logic to open chat can be added here
        } else {
            const targetLink = normalizeNotificationLink(notification);
            if (targetLink) {
                navigate(targetLink);
            }
        }
        setVisible(false);
    };

    const isNotificationUnread = (n) => n.is_read === 0 || n.is_read === false || (n.isRead === 0);

    const chatNotifications = notifications.filter(n => n.type === 'chat');
    const systemNotifications = notifications.filter(n => n.type !== 'chat');

    const renderNotificationList = (data, emptyText) => (
        <List
            dataSource={data}
            locale={{ emptyText: <Empty description={emptyText} /> }}
            renderItem={(item) => {
                const unread = isNotificationUnread(item);
                const { title: displayTitle, message: displayMessage } = translateNotification(item, t);
                return (
                    <List.Item
                        onClick={() => handleNotificationClick(item)}
                        style={{
                            cursor: "pointer",
                            padding: "16px",
                            backgroundColor: unread ? "#f0f7ff" : "transparent",
                            borderBottom: "1px solid #f0f0f0",
                            transition: "background-color 0.3s",
                        }}
                        className="hover:bg-gray-50">
                        <List.Item.Meta
                            avatar={
                                <Avatar
                                    size={40}
                                    icon={item.type === 'chat' ? <MessageOutlined /> : <BellOutlined />}
                                    style={{
                                        backgroundColor: unread ? (item.type === 'chat' ? "#3073f1" : "#1890ff") : "#d9d9d9",
                                    }}
                                />
                            }
                            title={
                                <div className="flex justify-between items-center mb-1">
                                    <Text strong={unread} className="text-[14px] leading-tight text-gray-900">
                                        {displayTitle}
                                    </Text>
                                    {unread && (
                                        <div className="w-2 h-2 bg-blue-500 rounded-full shrink-0" />
                                    )}
                                </div>
                            }
                            description={
                                <div>
                                    <div className="text-gray-500 text-[13px] mb-1.5 line-clamp-2 leading-relaxed">
                                        {displayMessage}
                                    </div>
                                    <Text type="secondary" style={{ fontSize: "11px", display: 'flex', alignItems: 'center' }}>
                                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                                        {dayjs(item.created_at).fromNow()}
                                    </Text>
                                </div>
                            }
                        />
                    </List.Item>
                );
            }}
        />
    );

    const content = (
        <div style={{ width: 380, maxHeight: 550, display: "flex", flexDirection: "column" }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px",
                    borderBottom: "1px solid #f0f0f0",
                    background: "#fff",
                }}>
                <Title level={5} style={{ margin: 0, fontWeight: 700 }}>
                    {t('notification.title')}
                </Title>
                <Button
                    type="link"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAllAsRead();
                    }}
                    disabled={unreadCount === 0}
                    className="p-0 h-auto font-medium text-blue-500 hover:text-blue-600 disabled:text-gray-300">
                    {t('notification.markAllAsRead')}
                </Button>
            </div>
            
            {loading ? (
                <div style={{ padding: 40, textAlign: "center" }}><Spin /></div>
            ) : (
                <Tabs
                    centered
                    className="notification-tabs"
                    items={[
                        {
                            key: "1",
                            label: (
                                <Badge count={systemNotifications.filter(isNotificationUnread).length} size="small" offset={[10, 0]} color="#1890ff">
                                    <span className="px-2 font-medium">System</span>
                                </Badge>
                            ),
                            children: (
                                <div style={{ maxHeight: 400, overflowY: "auto", overflowX: 'hidden' }}>
                                    {renderNotificationList(systemNotifications, t('notification.noNotifications'))}
                                </div>
                            )
                        },
                        {
                            key: "2",
                            label: (
                                <Badge count={chatNotifications.filter(isNotificationUnread).length} size="small" offset={[10, 0]} color="#3073f1">
                                    <span className="px-2 font-medium">Messages</span>
                                </Badge>
                            ),
                            children: (
                                <div style={{ maxHeight: 400, overflowY: "auto", overflowX: 'hidden' }}>
                                    {renderNotificationList(chatNotifications, "No message notifications")}
                                </div>
                            )
                        }
                    ]}
                />
            )}
            <style>{`
                .notification-tabs .ant-tabs-nav {
                    margin-bottom: 0 !important;
                    background: #fff;
                }
                .notification-tabs .ant-tabs-tab {
                    padding: 12px 0 !important;
                    transition: all 0.3s;
                }
                .notification-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
                    color: #1890ff !important;
                }
                .notification-tabs .ant-tabs-ink-bar {
                    background: #1890ff !important;
                }
            `}</style>
        </div>
    );


    return (
        <Popover
            content={content}
            trigger="click"
            open={visible}
            onOpenChange={setVisible}
            placement="bottomRight"
            arrow={{ pointAtCenter: true }}
            overlayClassName="notification-popover"
            overlayInnerStyle={{ padding: 0 }}>
            <Badge count={unreadCount} overflowCount={99} size="small" offset={[-2, 4]}>
                <Button
                    type="text"
                    icon={<BellOutlined className="text-xl" />}
                    className="flex items-center justify-center h-10 w-10 p-0 bg-white dark:bg-[#1C1B20] border border-gray-100 dark:border-gray-700 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
                />
            </Badge>
        </Popover>
    );
};

export default NotificationCenter;

